import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { UnreadIconComponent } from '@/app/components/icons/unread-icon.component';
import { InquiryProxy, InquiryViewModel } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { ChatComponent, SupportTicketChatState } from '../chat/chat.component';
import { isInquiryThinking, isInquiryUnread } from '../support-ticket-inquiry-status.util';
import { SupportTicketAnalyticsService } from '../support-ticket-analytics.service';
import { SupportChatBubbleStateService } from './support-chat-bubble-state.service';

const PILL_LABEL_FALLBACK = 'Chat with AI';
const UNREAD_POLL_INTERVAL_MS = 10_000;

/**
 * Floating support-chat bubble for the home page. Wraps the same
 * `dk-support-ticket-chat` used by the full-page `/support-ticket` module so
 * "create new chat" and messaging behave identically in both places - this
 * wrapper only adds the collapsed/expanded bubble chrome and keeps the chat
 * alive (open state + active inquiry) across navigation for the life of the
 * session, via `SupportChatBubbleStateService`. The chat panel stays mounted
 * while the bubble is minimized so an in-flight reply can still be reported.
 */
@Component({
  selector: 'dk-support-chat-bubble',
  imports: [SharedModule, TranslateModule, ChatComponent, UnreadIconComponent],
  providers: [InquiryProxy],
  templateUrl: './support-chat-bubble.component.html',
  styleUrl: './support-chat-bubble.component.less',
})
export class SupportChatBubbleComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Named separately from the enum. A same-named class field is shadowed under
   * `useDefineForClassFields` and the template then reads `Welcome` of undefined.
   */
  readonly welcomePanel = SupportTicketChatState.Welcome;

  @ViewChild(ChatComponent) private chat?: ChatComponent;

  private unreadPollSubscription: Subscription | null = null;

  constructor(
    public state: SupportChatBubbleStateService,
    private inquiryProxy: InquiryProxy,
    private translateService: TranslateService,
    private hostEl: ElementRef<HTMLElement>,
    private analytics: SupportTicketAnalyticsService
  ) {}

  ngOnInit(): void {
    if (!this.state.isOpen) {
      this.startUnreadPolling();
    }
  }

  ngAfterViewInit(): void {
    // Teleport the host to <body> so no scrollable layout ancestor (e.g.
    // `.page-layout__body { overflow-y: auto }`) can capture
    // `position: fixed` and make the bubble scroll with page content.
    if (this.hostEl.nativeElement.parentElement !== document.body) {
      document.body.appendChild(this.hostEl.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.stopUnreadPolling();
    this.hostEl.nativeElement.remove();
  }

  get pillLabel(): string {
    const translated = this.translateService.instant('doktar_app.support_ticket.bubble.chat_with_ai');
    return translated === 'doktar_app.support_ticket.bubble.chat_with_ai' ? PILL_LABEL_FALLBACK : translated;
  }

  openBubble(): void {
    this.state.open();
    this.stopUnreadPolling();
    this.analytics.trackAiExpanded();
    this.chat?.reconcileIfDeferred();
  }

  closeBubble(): void {
    this.state.close();
    this.startUnreadPolling();
    this.analytics.trackAiMinimized();
  }

  onChatCreated(inquiryId: string): void {
    this.state.setActiveInquiry(inquiryId);
  }

  private startUnreadPolling(): void {
    this.stopUnreadPolling();
    this.refreshUnreadState();
    this.unreadPollSubscription = timer(UNREAD_POLL_INTERVAL_MS, UNREAD_POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.inquiryProxy.inquiry_GetAll()))
      .subscribe({
        next: (res) => this.syncUnreadFromInquiries(res.result ?? []),
      });
  }

  private refreshUnreadState(): void {
    this.inquiryProxy.inquiry_GetAll().subscribe({
      next: (res) => this.syncUnreadFromInquiries(res.result ?? []),
    });
  }

  /**
   * Syncs the unread indicator from `GET /inquiry` (list), not `GET /inquiry/{id}`.
   * The by-id endpoint marks the conversation read, which is why polling used
   * to both miss first-response chats (no `activeInquiryId` yet) and refuse to
   * clear the dot after the user read the same chat on `/support-ticket`.
   */
  private syncUnreadFromInquiries(inquiries: InquiryViewModel[]): void {
    const watched = this.resolveWatchedInquiry(inquiries);
    if (!watched) {
      this.state.hasUnread = false;
      return;
    }

    if (!this.state.activeInquiryId && (isInquiryUnread(watched.status) || isInquiryThinking(watched.status))) {
      this.state.setActiveInquiry(watched.id);
    }

    this.state.hasUnread = isInquiryUnread(watched.status);
  }

  private resolveWatchedInquiry(inquiries: InquiryViewModel[]): InquiryViewModel | undefined {
    if (this.state.activeInquiryId) {
      const active = inquiries.find((inquiry) => inquiry.id === this.state.activeInquiryId);
      if (active) {
        return active;
      }
    }

    return pickLatestInquiry(inquiries);
  }

  private stopUnreadPolling(): void {
    if (this.unreadPollSubscription) {
      this.unreadPollSubscription.unsubscribe();
      this.unreadPollSubscription = null;
    }
  }
}

function pickLatestInquiry(inquiries: InquiryViewModel[]): InquiryViewModel | undefined {
  if (inquiries.length === 0) {
    return undefined;
  }

  return [...inquiries].sort((a, b) => (b.updatedAt?.toMillis() ?? 0) - (a.updatedAt?.toMillis() ?? 0))[0];
}
